/**
 * useAccounts Hook
 *
 * Fetches accounts in real-time from Firestore.
 * Uses cache so re-entering the Accounts page shows data immediately (no loading skeletons).
 * Handles account order migration for accounts without order field.
 */

import { useState, useEffect } from "react";
import type React from "react";
import { auth } from "../../../firebase";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  writeBatch,
  Timestamp,
  QuerySnapshot,
  DocumentSnapshot,
  Unsubscribe,
  FirestoreError,
  FieldValue,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { Account, AccountGroup } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";

const accountsCache = new Map<string, { accounts: Account[]; timestamp: number }>();
const CACHE_EXPIRY_MS = 5 * 60 * 1000;

/**
 * Hook to fetch accounts in real-time
 * @param workspaceId - Workspace ID
 * @returns Object with accounts array, loading state, and setter function
 */
export function useAccounts(workspaceId: string | undefined): {
  accounts: Account[];
  loading: boolean;
  setAccounts: React.Dispatch<React.SetStateAction<Account[]>>;
} {
  const cached = workspaceId ? accountsCache.get(workspaceId) : null;
  const hasValidCache = !!(cached && (Date.now() - cached.timestamp) < CACHE_EXPIRY_MS);

  const [accounts, setAccounts] = useState<Account[]>(hasValidCache && cached ? cached.accounts : []);
  const [loading, setLoading] = useState<boolean>(!hasValidCache);

  // Fetch accounts in real-time
  useEffect(() => {
    // Allow loading in demo mode or when authenticated
    if (!workspaceId || (!auth.currentUser && !isDemoMode() && !isBypassAuthEnabled())) {
      setLoading(false);
      return;
    }

    if (!hasValidCache) setLoading(true);
    // In demo mode, userId is not needed (we fetch all accounts)
    const userId = auth.currentUser?.uid;

    const handleSnapshot = (snapshot: QuerySnapshot): void => {
      const accountsList: Account[] = snapshot.docs.map((docSnap: DocumentSnapshot) => {
        const data = docSnap.data();
        if (!data) {
          throw new Error("Account data is missing");
        }
        const account: any = {
          id: docSnap.id,
          userId: data.userId as string,
          accountName: data.accountName as string,
          group: data.group as AccountGroup,
          amount: data.amount as number,
          currency: data.currency as string,
          description: data.description as string,
          createdAt: data.createdAt,
          isArchived: data.isArchived as boolean | undefined,
          archivedAt: data.archivedAt as Timestamp | FieldValue | undefined,
          details: (data.details as Record<string, any> | undefined) || undefined,
        };
        // Include order field if it exists
        if (data.order !== undefined) {
          account.order = data.order as number;
        }
        return account as Account;
      });
      
      // Sort by group manually (in case orderBy fails)
      accountsList.sort((a, b) => {
        const groupA = a.group || "";
        const groupB = b.group || "";
        return groupA.localeCompare(groupB);
      });
      
      // Migrate accounts without order field: assign order based on current position in group
      const accountsNeedingOrder: Array<{ accountId: string; group: string; index: number }> = [];
      const accountsByGroup: Record<string, Account[]> = {};
      
      accountsList.forEach((account) => {
        if (!account.isArchived) {
          const group = account.group as string;
          if (!accountsByGroup[group]) {
            accountsByGroup[group] = [];
          }
          accountsByGroup[group].push(account);
        }
      });
      
      // Check which accounts need order field assigned
      Object.entries(accountsByGroup).forEach(([group, groupAccounts]) => {
        groupAccounts.forEach((account, index) => {
          if ((account as any).order === undefined) {
            accountsNeedingOrder.push({ accountId: account.id, group, index });
            // Update local state immediately
            (account as any).order = index;
          }
        });
      });
      
      // If there are accounts without order, assign order values (async, don't block)
      if (accountsNeedingOrder.length > 0 && !isDemoMode() && auth.currentUser) {
        (async () => {
          try {
            const batch = writeBatch(db);
            accountsNeedingOrder.forEach(({ accountId, index }) => {
              const accountRef = doc(db, "workspaces", workspaceId!, "accounts", accountId);
              batch.update(accountRef, { order: index });
            });
            await batch.commit();
          } catch (error) {
            console.error("Error migrating account order fields:", error);
          }
        })();
      }
      
      setAccounts(accountsList);
      setLoading(false);
      accountsCache.set(workspaceId!, { accounts: accountsList, timestamp: Date.now() });
    };

    // Query accounts from workspace subcollection
    const accountsRef = collection(db, "workspaces", workspaceId, "accounts");
    // Fetch all accounts in workspace (for now - will add role-based permissions later)
    const q = query(accountsRef, orderBy("group", "asc"));

    let unsubscribe: Unsubscribe | undefined;

    try {
      unsubscribe = onSnapshot(
        q,
        handleSnapshot,
        (error: FirestoreError) => {
          console.error("Error fetching accounts:", error);
          // If error is about missing index, try simpler query without orderBy
          if (error.code === "failed-precondition" || error.message?.includes("index")) {
            console.log("Index missing, trying query without orderBy");
            const fallbackQ = query(accountsRef);
            
            unsubscribe = onSnapshot(
              fallbackQ,
              handleSnapshot,
              (fallbackError: FirestoreError) => {
                console.error("Fallback query also failed:", fallbackError);
                setLoading(false);
              }
            );
          } else {
            setLoading(false);
          }
        }
      );
    } catch (error) {
      console.error("Query setup error:", error);
      setLoading(false);
      return;
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [workspaceId]);

  return { accounts, loading, setAccounts };
}
